import { useSelector } from 'react-redux';

const isTruthyPermission = (value) => value === true || value === 'true';

/**
 * Private extension layer for attribute-specific permissions.
 * Keep explicit per-attribute overrides here to avoid scattering business rules.
 */
const attributePermissionResolvers = {
  accumulatorsEdit: (userAttributes = {}) => isTruthyPermission(userAttributes.accumulatorsEdit),
};

const evaluateAuthorization = ({
  administrator,
  serverReadonly,
  userReadonly,
  attributePermission,
}) => {
  if (administrator) {
    return true;
  }

  if (serverReadonly || userReadonly) {
    return false;
  }

  return !!attributePermission;
};

export const useAdministrator = () => useSelector((state) => {
  const admin = state.session.user.administrator;
  return admin;
});

export const useManager = () => useSelector((state) => {
  const admin = state.session.user.administrator;
  const manager = (state.session.user.userLimit || 0) !== 0;
  return admin || manager;
});

export const useDeviceReadonly = () => useSelector((state) => {
  const admin = state.session.user.administrator;
  const serverReadonly = state.session.server.readonly;
  const userReadonly = state.session.user.readonly;
  const serverDeviceReadonly = state.session.server.deviceReadonly;
  const userDeviceReadonly = state.session.user.deviceReadonly;
  return !admin && (serverReadonly || userReadonly || serverDeviceReadonly || userDeviceReadonly);
});

export const useRestriction = (key) => useSelector((state) => {
  const admin = state.session.user.administrator;
  const serverValue = state.session.server[key];
  const userValue = state.session.user[key];
  return !admin && (serverValue || userValue);
});

/**
 * Normalizes accumulators edit permission from user attributes.
 * Accepts both boolean true and string "true", otherwise returns false.
 */
export const useCanEditAccumulators = () => useSelector((state) => {
  const value = state.session.user?.attributes?.accumulatorsEdit;
  return isTruthyPermission(value);
});

/**
 * Authorization precedence:
 * 1) administrator can always edit;
 * 2) readonly in server/user blocks editing;
 * 3) attribute-specific permission can grant editing.
 */
export const useCanEditAccumulatorFields = () => useSelector((state) => {
  const administrator = state.session.user?.administrator;
  const serverReadonly = state.session.server?.readonly;
  const userReadonly = state.session.user?.readonly;
  const attributePermission = attributePermissionResolvers.accumulatorsEdit(state.session.user?.attributes);

  return evaluateAuthorization({
    administrator,
    serverReadonly,
    userReadonly,
    attributePermission,
  });
});
